import React from "react"
import * as Yup from "yup"
import {
  Text, VStack, Box,
} from "@chakra-ui/react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import SwitchField from "components/ReactHookForm/SwitchField"
import GoogleMap from "components/Portal/website/GoogleMap"
import Link from "components/elements/Link"
import { Website } from "sharedTypes"
import * as api from "api/websites"
import { updateWebsite } from "context/actions"
import { useQueryClient } from "react-query"
import { eventSettingsPath } from "utilities/routes"
import { MapSettingsFormValues } from "pages/EventShow/WebPage/types"

interface Props {
  eventId: string
  website: Website
  onClose: () => void
  onHide: () => void
  onChange?: (values: MapSettingsFormValues) => void
}

const MapSettings = ({
  eventId, website, onClose, onHide, onChange,
}: Props) => {
  const queryClient = useQueryClient()
  const hasAddress = Boolean(website.latitude && website.longitude)

  return (
    <ModalForm
      title="Map Settings"
      size={hasAddress ? "6xl" : "xl"}
      onClose={onClose}
      initialValues={{
        mapEnabled: website.mapEnabled ?? false,
      }}
      validationSchema={Yup.object().shape({ mapEnabled: Yup.boolean() })}
      onSubmit={(values: { mapEnabled: boolean }) => api.putWebsite(eventId, values)
        .then(({ data }) => {
          updateWebsite(queryClient, eventId, data)
          onHide()
        })}
      disableSubmitWhenNotDirty
      onChange={onChange}
    >
      {({ control, watch }) => {
        const mapEnabled = watch("mapEnabled")

        return (
          <VStack spacing={6} align="stretch">
            <SwitchField
              name="mapEnabled"
              control={control}
              label="Show Event Map"
              fontSize="2xl"
              fontWeight="normal"
              mb={0}
            />

            <Box>
              <Text fontSize="md" mb={4}>
                Display an interactive map at the bottom of your event website. It shows the
                venue name, full address, and a Directions button that opens Google Maps from
                the visitor&apos;s location.
              </Text>
              <Text fontSize="md">
                To update the venue or address, go to:{" "}
                <Link to={eventSettingsPath(eventId)}>
                  Event Settings
                </Link>
                .
              </Text>
            </Box>

            {hasAddress && (
              <Box
                borderRadius="md"
                overflow="hidden"
                borderWidth={1}
                borderColor="gray.200"
                opacity={mapEnabled ? 1 : 0.5}
                transition="opacity 0.2s"
              >
                <GoogleMap
                  website={{
                    ...website,
                    mapEnabled: true,
                  }}
                />
              </Box>
            )}
          </VStack>
        )
      }}
    </ModalForm>
  )
}

export default MapSettings
